import { computed, readonly, ref, useContext } from '@nuxtjs/composition-api';

import { withCheckoutCartWrite } from '~/helpers/checkout/withCartWrite';
import { Logger } from '~/helpers/logger';
import useCart from '~/modules/checkout/composables/useCart';
import { CartContextChangedError, cartReadContractKey, CartResultUnconfirmedError } from '~/modules/checkout/composables/useCart/cartCoordinator';
import type { ShippingCartAddress } from '~/modules/GraphQL/types';

import type { UseShippingErrors, UseShippingInterface, UseShippingLoadParams, UseShippingSaveParams } from './useShipping';

/**
 * Allows loading the shipping information for
 * the current cart and saving (selecting) other shipping information for the
 * same cart.
 *
 * See the {@link UseShippingInterface} for a list of methods and values available in this composable.
 */
export function useShipping(): UseShippingInterface {
  const loading = ref(false);
  let loadSequence = 0;
  const error = ref<UseShippingErrors>({ load: null, save: null });
  const { cart, load: loadCart, loading: isLoadingCart, setCart, isCurrentData, lastFullReadState, error: cartError } = useCart();
  const { app } = useContext();
  const owner = () => {
    const { state } = app.$vsf.$magento.config;
    return [state.getCartId?.() || '', state.getCustomerToken?.() || '', state.getStore?.() || '',
      state.getLocale?.() || '', state.getCurrency?.() || ''];
  };

  const load = async (params: UseShippingLoadParams = {}): Promise<ShippingCartAddress | null> => {
    Logger.debug('useShipping.load');
    let shippingInfo: any | null = null;
    const sequence = ++loadSequence;
    const requestedOwner = owner();
    const requestedContract = cartReadContractKey(params);

    try {
      loading.value = true;
      const customRead = Object.keys(params.customQuery ?? {}).length > 0
        || Object.keys(params.customHeaders ?? {}).length > 0;
      if (!cart?.value?.shipping_addresses?.length || !isCurrentData?.() || customRead) {
        await loadCart({ ...params, reuseExisting: true });
        if (lastFullReadState?.value !== 'applied' || !isCurrentData?.(params)) {
          throw cartError?.value?.load ?? new CartContextChangedError();
        }
      }
      // Even the already-loaded fast path crosses an async boundary. Check
      // identity/provenance after that boundary before returning private data.
      await Promise.resolve();
      const currentOwner = owner();
      if (!isCurrentData?.(params) || requestedContract !== cartReadContractKey(params)
        || requestedOwner.some((value, index) => (index !== 0 || value !== '') && value !== currentOwner[index])) {
        throw new CartContextChangedError();
      }
      [shippingInfo = null] = cart.value?.shipping_addresses ?? [];
      if (sequence === loadSequence) error.value.load = null;
    } catch (err) {
      if (sequence === loadSequence) error.value.load = err;
      Logger.error('useShipping/load error: ', err);
    } finally {
      if (sequence === loadSequence) loading.value = false;
    }
    return shippingInfo;
  };

  const save = async ({ shippingDetails }: UseShippingSaveParams): Promise<ShippingCartAddress | null> => {
    Logger.debug('useShipping.save:', { shippingDetails });
    let shippingInfo: ShippingCartAddress | null = null;

    if (loading.value || isLoadingCart.value) return null;
    try {
      loading.value = true;
      error.value.save = null;

      const { id } = cart.value;
      shippingInfo = await withCheckoutCartWrite(app, id, async (guard) => {
        const { apartment, neighborhood, extra, customerAddressId, ...address } = shippingDetails;
        if (address && (address.region_name || address.region_name === '')) {
          delete address.region_name;
        }

        const shippingData = customerAddressId
          ? { customer_address_id: customerAddressId }
          : {
            address: {
              ...address,
              street: [address.street, apartment, neighborhood, extra].filter(Boolean),
            },
          };

        const shippingAddressInput = {
          cart_id: id,
          shipping_addresses: [
            {
              ...shippingData,
            },
          ],
        };
        const { data, errors } = await app.$vsf.$magento.api.setShippingAddressesOnCart(shippingAddressInput, {
          setShippingAddressesOnCart: 'cart-set-shipping-addresses',
        });

        Logger.debug('[SetShippingAddressesOnCart Result]:', { data, errors });
        guard.assertCurrent();
        const cartData: any = data?.setShippingAddressesOnCart?.cart;
        if (errors?.length) {
          if (cartData) throw new CartResultUnconfirmedError(errors[0]?.message);
          throw new Error(errors[0]?.message || 'Error setting shipping address');
        }
        if (!cartData?.shipping_addresses?.[0] || cartData.id !== id || !cartData.prices
          || !Array.isArray(cartData.available_payment_methods)) throw new CartResultUnconfirmedError();
        if (cartData) {
          setCart({
            ...cart.value,
            shipping_addresses: cartData.shipping_addresses,
            available_payment_methods: cartData.available_payment_methods,
            prices: cartData.prices,
          });
        // setCart(data.setShippingAddressesOnCart.cart as unknown as Cart);
        }

        const [confirmedShipping] = data.setShippingAddressesOnCart.cart.shipping_addresses;
        return confirmedShipping;
      });
      // if (cart?.value?.id) {
      //   await loadCart();
      // }

      error.value.save = null;
    } catch (err) {
      error.value.save = err;
      Logger.error('useShipping/save', err);
    } finally {
      loading.value = false;
    }

    return shippingInfo;
  };
  return {
    load,
    save,
    error: readonly(error),
    loading: computed(() => loading.value || isLoadingCart.value),
  };
}

export * from './useShipping';
export default useShipping;
